import type { Campaign } from '@sola/schema/dotcom/shared/content/campaigns.ts';
import { ContentTypesEnum } from '@sola/schema/dotcom/shared/content/content.ts';
import type { GlobalSettings } from '@sola/schema/dotcom/shared/content/settings.ts';
import type { GetOptions } from '@sola/shared-utils/httpClient.ts';
import {
  httpDelete,
  httpGet,
  httpPost,
} from '@sola/shared-utils/httpClient.ts';

import type {
  CreateOutageBannerInput,
  DeleteOutageBannerInput,
  OutageBanner,
  OutageBannerList,
  OutageBannerSite,
} from '../lib/outage-banner/schema';

const contentRouteBase =
  process.env.SDG_API_PROXY_DESTINATION ||
  process.env.NEXT_PUBLIC_BASE_URL_SDG ||
  process.env.BASE_URL_SDG ||
  'https://sdg.compassion.com';

const corsOptions: GetOptions = {
  credentials: 'include',
};

export const fetchActiveCampaign = async (): Promise<Campaign | undefined> => {
  return await httpGet<Campaign>(
    `${contentRouteBase}/${ContentTypesEnum.Campaigns}/active`,
    corsOptions,
  );
};

export const getCampaignDetails = async (
  campaignId: string,
): Promise<Campaign | undefined> => {
  return await httpGet<Campaign>(
    `${contentRouteBase}/${ContentTypesEnum.Campaigns}/${campaignId}`,
  );
};

export const searchCampaignDetails = async (
  queryParams: Record<string, string> | URLSearchParams,
): Promise<Campaign | undefined> => {
  const urlParams = new URLSearchParams(queryParams);
  return await httpGet<Campaign>(
    `${contentRouteBase}/${ContentTypesEnum.Campaigns}`,
    { queryParams: urlParams },
  );
};

export const getSettings = async (): Promise<GlobalSettings | undefined> => {
  return await httpGet<GlobalSettings>(
    `${contentRouteBase}/${ContentTypesEnum.Settings}`,
  );
};

export const getSettingsByKey = async <T>(settingsKey: string) => {
  return await httpGet<T>(
    `${contentRouteBase}/${ContentTypesEnum.Settings}/${settingsKey}`,
  );
};

export const createOutageBanner = async (body: CreateOutageBannerInput) => {
  return await httpPost<OutageBanner>(
    `${contentRouteBase}/${ContentTypesEnum.OutageBanners}/create`,
    body,
  );
};

export const fetchOutageBanners = async (
  site?: OutageBannerSite,
  active?: boolean,
) => {
  const params = new URLSearchParams();

  if (active) {
    params.set('active', String(active));
  }

  if (site?.length) {
    params.set('site', site.join(','));
  }

  return await httpGet<OutageBannerList>(
    `${contentRouteBase}/${ContentTypesEnum.OutageBanners}?${params.toString()}`,
  );
};

export const deleteOutageBanner = async (input: DeleteOutageBannerInput) => {
  return await httpDelete<DeleteOutageBannerInput>(
    `${contentRouteBase}/${ContentTypesEnum.OutageBanners}/delete/${input}`,
  );
};
